import { ukDate } from "../lib/dates";
import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { HandCoins, Heart, TrendingUp, Plus, CreditCard, Banknote, Download } from "lucide-react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const inputCls = "w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 focus:border-[#0B5E4E] outline-none transition-all px-4 py-2.5 text-sm";
const PIE = ["#0B5E4E", "#17A88A", "#084438", "#A8A29E", "#79C0AE", "#084438"];
const PRESETS = [10, 25, 50, 100];

import { GiftAidBox, GiftAidFields, GiftAidClaim, useGiftAid, giftAidComplete } from "../components/GiftAid";

const MANUAL_EMPTY = { donor_name: "", amount: "", fund: "Offering", method: "Cash", date: "", gift_aid: false, gift_aid_details: { title: "", first_name: "", last_name: "", house: "", postcode: "" } };

export default function Giving() {
  const { user } = useAuth();
  const canViewGiving = !!user?.caps?.view_donations;
  const [data, setData] = useState(null);
  const [funds, setFunds] = useState(["Offering"]);
  const [amount, setAmount] = useState(25);
  const [fund, setFund] = useState("Offering");
  const [donorName, setDonorName] = useState("");
  const [processing, setProcessing] = useState(false);
  const [manual, setManual] = useState(MANUAL_EMPTY);
  const ga = useGiftAid();

  const load = useCallback(async () => {
    try { const { data } = await api.get("/donations"); setData(data); setFunds(data.funds); }
    catch { /* members may not have access to list */ setFunds(["Tithe", "Offering", "Building Fund", "Missions", "Welfare", "Thanksgiving"]); }
  }, []);

  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 6) { toast.warning("Still processing — please check back shortly."); return; }
    try {
      const { data } = await api.get(`/donations/checkout/status/${sessionId}`);
      if (data.payment_status === "paid") { toast.success("Thank you for your generous gift! 🙏"); load(); return; }
      if (data.status === "expired") { toast.error("Payment session expired."); return; }
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch { setTimeout(() => pollStatus(sessionId, attempts + 1), 2000); }
  }, [load]);

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (sid) {
      toast.info("Confirming your payment...");
      pollStatus(sid);
      window.history.replaceState({}, "", "/giving");
    }
  }, [load, pollStatus]);

  const give = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt < 1) return toast.error("Enter a valid amount");
    if (!(await ga.persist())) return;
    setProcessing(true);
    try {
      const { data } = await api.post("/donations/checkout", {
        amount: amt, fund, donor_name: donorName, origin_url: window.location.origin, gift_aid: ga.on,
      });
      window.location.href = data.url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setProcessing(false); }
  };

  const recordManual = async (e) => {
    e.preventDefault();
    if (manual.gift_aid && !giftAidComplete(manual.gift_aid_details)) return toast.error("Add the giver's name, house and postcode for Gift Aid");
    try {
      await api.post("/donations/manual", { ...manual, amount: parseFloat(manual.amount), gift_aid_details: manual.gift_aid ? manual.gift_aid_details : null });
      toast.success("Donation recorded"); setManual(MANUAL_EMPTY); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/donations/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `giving_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Giving exported");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="sj-page-hero">
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Giving</h1>
        <p style={{ color: "var(--sj-text-2)" }}>Support the work of God — securely online</p>
      </div>

      {canViewGiving && data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className={`${CARD} p-6`} data-testid="giving-total">
            <HandCoins className="h-7 w-7" style={{ color: "#0B5E4E" }} />
            <div className="mt-3 font-head text-3xl font-extrabold">£{data.total.toLocaleString('en-GB')}</div>
            <div className="text-sm" style={{ color: "var(--sj-text-2)" }}>Total Received</div>
          </div>
          <div className={`${CARD} p-6`}>
            <TrendingUp className="h-7 w-7" style={{ color: "#17A88A" }} />
            <div className="mt-3 font-head text-3xl font-extrabold">£{data.this_month.toLocaleString('en-GB')}</div>
            <div className="text-sm" style={{ color: "var(--sj-text-2)" }}>This Month</div>
          </div>
          <div className={`${CARD} p-6`}>
            <Heart className="h-7 w-7" style={{ color: "#084438" }} />
            <div className="mt-3 font-head text-3xl font-extrabold">{data.count}</div>
            <div className="text-sm" style={{ color: "var(--sj-text-2)" }}>Gifts Recorded</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Give online */}
        <div className={`${CARD} p-6 ${canViewGiving ? "lg:col-span-1" : "lg:col-span-2"}`}>
          <div className="flex items-center gap-2 mb-4"><CreditCard className="h-5 w-5" style={{ color: "var(--sj-primary)" }} /><h3 className="font-head text-xl font-semibold">Give Online</h3></div>
          <form onSubmit={give} className="space-y-4">
            <div className="grid grid-cols-4 gap-2">
              {PRESETS.map((p) => (
                <button type="button" key={p} data-testid={`preset-${p}`} onClick={() => setAmount(p)}
                  className="rounded-xl py-2.5 font-semibold border transition-all"
                  style={Number(amount) === p ? { background: "var(--sj-primary)", color: "#fff", borderColor: "var(--sj-primary)" } : { background: "#fff", borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
                  £{p}
                </button>
              ))}
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Amount (£)</label>
              <input data-testid="give-amount-input" type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Fund</label>
              <select data-testid="give-fund-select" value={fund} onChange={(e) => setFund(e.target.value)} className={inputCls}>
                {funds.map((f) => <option key={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Name (optional)</label>
              <input data-testid="give-name-input" placeholder="Your name" value={donorName} onChange={(e) => setDonorName(e.target.value)} className={inputCls} />
            </div>
            <GiftAidBox ga={ga} amount={amount} />
            <button type="submit" disabled={processing} data-testid="give-submit-button" className="w-full flex items-center justify-center gap-2 rounded-full py-3 text-white font-semibold shadow-sm hover:brightness-105 transition-all disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>
              <HandCoins className="h-5 w-5" /> {processing ? "Redirecting..." : `Give £${amount || 0}`}
            </button>
            <p className="text-xs text-center" style={{ color: "var(--sj-text-3)" }}>Secure payment powered by Stripe</p>
          </form>
        </div>

        {canViewGiving && data && (
          <>
            <div className={`${CARD} p-6`}>
              <h3 className="font-head text-xl font-semibold mb-2">Giving by Fund</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={data.by_fund} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2}>
                    {data.by_fund.map((entry, i) => <Cell key={entry.name || `fund-${i}`} fill={PIE[i % PIE.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `£${v}`} contentStyle={{ borderRadius: 12, border: "1px solid #EBE9E4" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className={`${CARD} p-6`}>
              <div className="flex items-center gap-2 mb-4"><Banknote className="h-5 w-5" style={{ color: "var(--sj-secondary)" }} /><h3 className="font-head text-xl font-semibold">Record Offline Gift</h3></div>
              <form onSubmit={recordManual} className="space-y-3">
                <input data-testid="manual-donor-input" required placeholder="Donor name" value={manual.donor_name} onChange={(e) => setManual({ ...manual, donor_name: e.target.value })} className={inputCls} />
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="manual-amount-input" required type="number" step="0.01" placeholder="Amount £" value={manual.amount} onChange={(e) => setManual({ ...manual, amount: e.target.value })} className={inputCls} />
                  <input type="date" value={manual.date} onChange={(e) => setManual({ ...manual, date: e.target.value })} className={inputCls} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <select value={manual.fund} onChange={(e) => setManual({ ...manual, fund: e.target.value })} className={inputCls}>{funds.map((f) => <option key={f}>{f}</option>)}</select>
                  <select value={manual.method} onChange={(e) => setManual({ ...manual, method: e.target.value })} className={inputCls}><option>Cash</option><option>Bank Transfer</option><option>Cheque</option></select>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid="manual-gift-aid-checkbox" checked={manual.gift_aid} onChange={(e) => setManual({ ...manual, gift_aid: e.target.checked })} className="h-4 w-4 accent-[#0B5E4E]" />
                  Giver has made a Gift Aid declaration
                </label>
                {manual.gift_aid && <GiftAidFields prefix="manual-gift-aid" value={manual.gift_aid_details} onChange={(v) => setManual({ ...manual, gift_aid_details: v })} />}
                <button type="submit" data-testid="record-manual-button" className="w-full flex items-center justify-center gap-2 rounded-full py-2.5 text-white font-semibold hover:brightness-105 transition-all" style={{ background: "var(--sj-secondary)" }}><Plus className="h-4 w-4" /> Record Gift</button>
              </form>
            </div>
          </>
        )}
      </div>

      {canViewGiving && data && <GiftAidClaim data={data} />}

      {canViewGiving && data && (
        <div className={`${CARD} p-6`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-head text-xl font-semibold">Recent Gifts</h3>
            <button data-testid="export-giving-button" onClick={exportCsv}
              className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium border transition-all hover:bg-[#EDF2F4]"
              style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>
          <div className="overflow-x-auto" data-testid="donations-table">
            <table className="w-full text-sm">
              <thead><tr className="text-left" style={{ background: "var(--sj-bg)", color: "var(--sj-text-2)" }}>
                <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider">Donor</th>
                <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider">Fund</th>
                <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider">Method</th>
                <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider">Date</th>
                <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider">Gift Aid</th>
                <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-right">Amount</th>
              </tr></thead>
              <tbody>
                {data.donations.slice(0, 20).map((d) => (
                  <tr key={d.id} className="border-t hover:bg-[#EDF2F4]" style={{ borderColor: "var(--sj-border)" }}>
                    <td className="py-3 px-4 font-medium">{d.donor_name}</td>
                    <td className="py-3 px-4" style={{ color: "var(--sj-text-2)" }}>{d.fund}</td>
                    <td className="py-3 px-4" style={{ color: "var(--sj-text-2)" }}>{d.method}</td>
                    <td className="py-3 px-4" style={{ color: "var(--sj-text-2)" }}>{ukDate(d.date)}</td>
                    <td className="py-3 px-4" data-testid={`donation-gift-aid-${d.id}`}>{d.gift_aid ? <span className="text-xs font-semibold rounded-full px-2 py-0.5" style={{ background: "#FEF6D8", color: "#8a6a00" }}>Yes</span> : <span style={{ color: "var(--sj-text-3)" }}>—</span>}</td>
                    <td className="py-3 px-4 text-right font-semibold" style={{ color: "var(--sj-secondary)" }}>£{Number(d.amount).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
